import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import TabPanel from "@mui/lab/TabPanel";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";

import CheckboxFormField from "components/checkbox-form-field";

import type { SelectSaleConfigurationValues } from "../word-processor-select-sale-configuration-dialog.component";
import SettingsAccordionSummary from "./settings-accordion-summary.component";

interface ServicesSettingsProps {
  readonly expanded?: boolean;
  readonly onChange?: (event: React.SyntheticEvent, expanded: boolean) => void;
}
export default function ServicesSettings({
  expanded,
  onChange
}: ServicesSettingsProps) {
  const { control, setValue } = useFormContext<SelectSaleConfigurationValues>();

  const servicesFieldNames = [
    "services_show_cpt_code",
    "services_show_product",
    "services_show_description",
    "services_show_price",
    "services_show_quantity"
  ] as const;

  const watchedValues = useWatch({
    control,
    name: servicesFieldNames
  });

  const selectedCount = useMemo(() => {
    return Array.isArray(watchedValues)
      ? watchedValues.filter(Boolean).length
      : 0;
  }, [watchedValues]);

  const handleSelectAll = useCallback(
    (event: any) => {
      event.preventDefault();
      event.stopPropagation();
      (setValue as any)("services_show_cpt_code", true);
      (setValue as any)("services_show_product", true);
      (setValue as any)("services_show_description", true);
      (setValue as any)("services_show_price", true);
      (setValue as any)("services_show_quantity", true);
    },
    [setValue]
  );

  const handleDeselectAll = useCallback(
    (event: any) => {
      event.preventDefault();
      event.stopPropagation();
      (setValue as any)("services_show_cpt_code", false);
      (setValue as any)("services_show_product", false);
      (setValue as any)("services_show_description", false);
      (setValue as any)("services_show_price", false);
      (setValue as any)("services_show_quantity", false);
    },
    [setValue]
  );

  return (
    <TabPanel value="services">
      <Accordion expanded={expanded} onChange={onChange}>
        <SettingsAccordionSummary
          title="Invoice Services settings:"
          expanded={expanded}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          selectedCount={selectedCount}
        />
        <AccordionDetails>
          <Stack ml={2} mt={-2} mb={2}>
            <FormControlLabel
              label="Show CPT Code"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="services_show_cpt_code"
                  control={control}
                />
              }
            />
            <FormControlLabel
              label="Show Product"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="services_show_product"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Show Description"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="services_show_description"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Show Price"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="services_show_price"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Show Quantity"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="services_show_quantity"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </TabPanel>
  );
}
